<script lang="ts">
  import { ComponentStatus } from '$lib/components';
  import { AnchorInline } from '$lib/components/anchor';
  import { Button } from '$lib/components/button';
  import { DividerHorizontal } from '$lib/components/divider';
  import { StepperStep } from '$lib/components/stepper';
  import { TextCredit, TextParagraph, TextStatus } from '$lib/components/text';
  import { AppPath, FREE_PLAN_BULK_LIMIT, FREE_PLAN_RATE_LIMIT, LIST_SIZE_LIMIT } from '$lib/features/app';
  import type { MeListEstimateDto, ListJobParamsDto, MeListPreprocessDto } from '$lib/features/bff/sdk';
  import { formatNumber } from '$lib/features/format';
  import { me } from '$lib/stores';

  /** Required */
  export let estimate: MeListEstimateDto;
  /** Default: `0` */
  export let fixedCost = 0;
  /** Required */
  export let jobParams: ListJobParamsDto;
  /** Required */
  export let size: number;
  /** Default: `undefined` */
  export let preprocess: MeListPreprocessDto = undefined;

  let creditsCost: number;

  $: totalMinutes = size && Math.round(Math.ceil(size / $me?.rateLimit));
  $: hours = totalMinutes && Math.floor(totalMinutes / 60);
  $: minutes = totalMinutes && totalMinutes % 60;

  $: meCredits = $me?.metadata?.creditsTotal;
  $: isActive = $me?.status == 'ACTIVE';
  $: hasLimit = $me?.rateLimit !== FREE_PLAN_RATE_LIMIT || size <= FREE_PLAN_BULK_LIMIT || fixedCost;
  $: hasCredits = meCredits && meCredits >= Math.max(creditsCost, FREE_PLAN_RATE_LIMIT);
  $: isOverLimit = size > LIST_SIZE_LIMIT;

  /**
   * Trigger estimate client side update
   */
  $: if (estimate) {
    if (fixedCost) {
      creditsCost = fixedCost;
    } else {
      const { simples, simplesHistory, registrations, suframa, geocoding } = jobParams;

      creditsCost = estimate.records.find((r) => r.feature === 'RFB').credits;

      if (simples) {
        creditsCost += estimate.records.find((r) => r.feature === 'SIMPLES').credits;
      }

      if (simplesHistory) {
        creditsCost += estimate.records.find((r) => r.feature === 'SIMPLES_HISTORY').credits;
      }

      if (registrations) {
        creditsCost += estimate.records.find((r) => r.feature === 'CCC').credits;
      }

      if (suframa) {
        creditsCost += estimate.records.find((r) => r.feature === 'SUFRAMA').credits;
      }

      if (geocoding) {
        creditsCost += estimate.records.find((r) => r.feature === 'MAP_GEOCODING').credits;
      }
    }
  }

  /**
   * Keep only the first itens in order to be able to
   * process in free plan
   */
  function sliceItens() {
    preprocess.valid = preprocess.valid.slice(0, FREE_PLAN_BULK_LIMIT);
    size = FREE_PLAN_BULK_LIMIT;
  }
</script>

<StepperStep locked={!estimate || !isActive || !hasLimit || !hasCredits || isOverLimit} title="Confirmação">
  {#if !estimate}
    <TextStatus status={ComponentStatus.PROCESSING} label="Estimando custo..." margin="mb-44" />
  {:else}
    <TextParagraph>
      Você está processando {formatNumber(size)} estabelecimentos, o custo estimado de acordo com suas seleções é de
      <TextCredit credits={creditsCost} />.
    </TextParagraph>

    <TextParagraph>
      Seus créditos não serão debitados imediatamente, mas sim ao longo do processamento. Caso inicie múltiplas listas,
      a conclusão de cada uma está sujeita a disponibilidade de créditos no momento.
    </TextParagraph>

    {#if hasCredits}
      <TextParagraph>
        Baseado em seu limite atual de {$me?.rateLimit} consultas por minuto, a exportação deve ser concluída em aproximadamente

        <b class={hours >= 8 ? 'text-warning-700-200-token' : 'text-success-500-400-token'}>
          {hours ? `${hours} ${hours === 1 ? 'hora' : 'horas'} e ` : ''}{minutes}
          {minutes === 1 ? 'minuto' : 'minutos'}</b
        >.
      </TextParagraph>
    {/if}

    {#if !isActive}
      <DividerHorizontal />
      <TextStatus status={ComponentStatus.WARNING} label="Conta de usuário limitada" />
      <TextParagraph>Para prosseguir, por favor, confirme seu e-mail.</TextParagraph>
    {:else if isOverLimit}
      <DividerHorizontal />
      <TextStatus status={ComponentStatus.WARNING} label="Limite de {formatNumber(LIST_SIZE_LIMIT)} itens excedido" />
    {:else if !hasLimit && !fixedCost}
      <DividerHorizontal />
      <TextStatus status={ComponentStatus.WARNING} label="Tamanho da lista excede limite do plano gratuito" />

      <TextParagraph>
        No plano gratuito é possível processar até {formatNumber(FREE_PLAN_BULK_LIMIT)} items. Para prosseguir, por favor,
        realize uma nova
        <AnchorInline target="_blank" label="assinatura" href={AppPath.SUBSCRIPTION_CHECKOUT} />.
      </TextParagraph>

      {#if preprocess}
        Ou

        <Button
          label="clique aqui"
          padding="p-0"
          variant="text-primary-500-400-token"
          font="font-medium"
          onClick={sliceItens}
        />

        para processar apenas os primeiros {FREE_PLAN_BULK_LIMIT} itens.
      {/if}
    {:else if !hasCredits}
      <DividerHorizontal />

      <TextStatus status={ComponentStatus.WARNING} label="Saldo insuficiente para iniciar o processamento" />

      <TextParagraph>
        Para prosseguir, por favor, realize uma nova
        <AnchorInline target="_blank" label="assinatura" href={AppPath.SUBSCRIPTION_CHECKOUT} />.
      </TextParagraph>
    {/if}
  {/if}
</StepperStep>
